import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Download, Inbox, Plus, Sparkles } from 'lucide-react';
import { api } from '../api/client';
import { naviguer } from '../App';
import { useRole } from '../context/RoleContext';
import { useMeta } from '../components/demande/useMeta';
import { PoleDot } from '../components/demande/PoleDot';
import { OrigineBadge, StatutBadge, TypeBadge } from '../components/demande/StatutBadge';
import { GroupesAbsenceTipee } from '../components/demande/GroupeAbsenceTipee';
import { VosRemplacements } from '../components/demande/VosRemplacements';
import { TableauCalendrier } from '../components/demande/TableauCalendrier';
import { BandeauUrgences, seancesACouvrir } from '../components/demande/BandeauUrgences';
import { MiniFrise } from '../components/demande/MiniFrise';
import { remplacantEnVigueur } from '../components/demande/elementsJour';
import { effacerRetourCalendrier, lireRetourCalendrier } from '../components/demande/retourCalendrier';
import { DemandesCollegues } from '../components/demande/DemandesCollegues';
import { useMaPersonne } from '../components/demande/useMaPersonne';
import { jourLocalIso } from '../components/demande/horaire';
import {
  classeEcheance, formatDateCourt,
  type MetaEtat, META_ETATS_ORDRE, META_ETAT_LABEL, META_ETAT_STATUTS, metaEtatDeStatuts,
} from '../components/demande/format';
import {
  type SeuilsUrgence, SEUILS_URGENCE_PAR_DEFAUT, STATUTS_NON_RESOLUS, joursAvant, niveauUrgence, NIVEAU_URGENCE_INFO, libelleEcheance, seuilsDepuisSettings,
} from '../components/demande/urgence';
import type { Action, Demande, Statut } from '../types';

interface Filtres {
  statuts: Statut[];
  pole_id: number | '';
  mois: string;
  miennes: boolean;
  actives: boolean;
  echeanceDepassee: boolean;
  urgenceActive: boolean;
  corbeille: boolean;
  // Origine des demandes (gestion) : filtre purement côté client, sur la liste déjà
  // chargée — « tipee » = créées par l'app à partir d'une absence Tipee ;
  // « main » = tout le reste (saisies à la main, urgences, import).
  origine: 'toutes' | 'main' | 'tipee';
  // Onglet propre aux intervenant·e·s (peutGerer voit toujours tout via
  // `miennes`) : 'miennes' = ses propres demandes (comportement historique,
  // inchangé) ; 'collegues' = les demandes en recherche des collègues, tous pôles
  // par défaut (périmètre réglable par l'admin : server/utils/access.ts::resoudreCollegues).
  vue: 'miennes' | 'collegues';
}

// Un·e intervenant·e (sans rôle de gestion) voit par défaut QUE ses propres
// demandes — le toggle « Mes demandes » n'est rendu que pour peutGerer, voir
// plus bas. L'onglet « Demandes des collègues » (vue ci-dessus) ajoute les demandes
// EN RECHERCHE des collègues, en lecture limitée (jamais la liste complète de la gestion).
// Filtres de « Mes demandes » qui n'ont pas de contrôle visible dans l'onglet des collègues : on les remet à
// zéro en changeant d'onglet, sinon la liste des collègues resterait vide ou tronquée sans qu'on sache pourquoi.
const FILTRES_VIERGES = { statuts: [] as Statut[], echeanceDepassee: false, urgenceActive: false };

function filtresInitiaux(peutGerer: boolean): Filtres {
  return { statuts: [], pole_id: '', mois: '', miennes: !peutGerer, actives: true, echeanceDepassee: false, urgenceActive: false, corbeille: false, origine: 'toutes', vue: 'miennes' };
}

const STATUTS_TERMINAUX: Statut[] = [
  'absence_validee', 'cloturee', 'fusionnee', 'annulee', 'maintenue', 'refusee', 'sans_suite', 'atelier_donne',
];

// Actions offertes en masse depuis la sélection du tableau — le sous-ensemble
// le plus utile pour vider la file « à traiter » (brouillon/soumise) sans
// ouvrir chaque fiche. D'autres actions restent réservées à la fiche demande.
const ACTIONS_GROUPEES: { action: Action; label: string; danger?: boolean }[] = [
  { action: 'soumettre', label: 'Soumettre' },
  { action: 'lancer_recherche', label: 'Lancer la recherche' },
  { action: 'valider_absence_simple', label: "Valider l'absence" },
  { action: 'ecarter', label: 'Écarter', danger: true },
];

// Conséquence en clair, affichée avant une action en masse.
const DESCRIPTIONS_GROUPEES: Partial<Record<Action, string>> = {
  soumettre: 'Les demandes seront envoyées et entreront dans le circuit de traitement.',
  lancer_recherche: 'Pour chaque demande, un 1er message est préparé sur Slack et la recherche de remplaçant·e démarre.',
  valider_absence_simple: "L'absence sera validée pour chaque demande, sans recherche de remplaçant·e.",
  ecarter: 'Les demandes seront classées sans suite (sans objet). Action définitive.',
};

type ColTri = 'urgence' | 'date' | 'atelier' | 'absent' | 'remplacant' | 'statut' | 'echeance';

function moisCourantStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function paramsDepuisFiltres(f: Filtres, vueIntervenant = false): Record<string, string | number | boolean | undefined> {
  const params: Record<string, string | number | boolean | undefined> = {};
  if (f.statuts.length) params.statut = f.statuts.join(',');
  if (f.pole_id !== '') params.pole_id = f.pole_id;
  if (f.miennes) params.miennes = true;
  if (f.actives) params.actives = true;
  if (f.echeanceDepassee) params.echeance_depassee = true;
  if (f.urgenceActive) params.urgence_active = true;
  if (f.corbeille) params.corbeille = true;
  if (f.vue === 'collegues') params.vue = 'collegues';
  // `vue=miennes` n'est envoyé QUE depuis la vue intervenant·e (réelle ou en
  // aperçu) : côté serveur il restreint aux demandes dont je suis la personne
  // absente, sans le OR created_by du filtre `miennes` historique. Ne jamais
  // l'envoyer depuis la vue gestion — f.vue vaut 'miennes' par défaut même
  // pour un compte gestion, qui verrait sa liste complète amputée.
  else if (vueIntervenant) params.vue = 'miennes';
  // Le champ mois peut recevoir une valeur invalide (certains navigateurs
  // rendent <input type="month"> comme un champ texte libre) : on ne construit
  // les bornes que si le format AAAA-MM est valide, sinon on ignore le filtre.
  // Sans ce garde-fou, `new Date(NaN,…).toISOString()` lève une exception non
  // rattrapée dans l'effet de chargement → l'app se démonte (page blanche).
  const moisMatch = /^(\d{4})-(\d{2})$/.exec(f.mois);
  if (moisMatch) {
    const annee = Number(moisMatch[1]);
    const mois = Number(moisMatch[2]);
    if (mois >= 1 && mois <= 12) {
      const du = new Date(annee, mois - 1, 1);
      const au = new Date(annee, mois, 0);
      params.du = du.toISOString().slice(0, 10);
      params.au = au.toISOString().slice(0, 10);
    }
  }
  return params;
}

export function TableauPage({ params }: { params?: Record<string, string> } = {}) {
  const { meta } = useMeta();
  const { peutGerer } = useRole();
  // Préremplissage depuis un lien profond (ex. clic sur le nom d'un·e
  // intervenant·e depuis une fiche demande : #tableau?q=Nom) — on ouvre alors
  // sur tout l'historique de la personne, donc « Actives seulement » désactivé.
  const prefillQ = params?.q ?? '';
  // Lien du message Slack : #tableau?vue=collegues&demande=ID → onglet « Demandes des
  // collègues », cette demande épinglée en haut (la gestion, elle, va droit à la fiche : plus bas).
  const lienCollegues = params?.vue === 'collegues';
  const epingleId = lienCollegues && /^\d+$/.test(params?.demande ?? '') ? Number(params?.demande) : null;
  const [filtres, setFiltres] = useState<Filtres>(() => {
    const base = filtresInitiaux(peutGerer);
    const f: Filtres = lienCollegues && !peutGerer ? { ...base, vue: 'collegues' } : base;
    return prefillQ ? { ...f, actives: false } : f;
  });
  // Recherche texte — purement côté client, sur la liste déjà chargée (mêmes
  // filtres pôle/état/mois/etc. appliqués avant) : pas d'aller-retour serveur
  // à chaque frappe, cohérent avec les pages Remplaçants/Ateliers.
  const [q, setQ] = useState(prefillQ);
  // Bascule Liste/Calendrier — remplace l'ancienne page autonome Calendrier
  // (supprimée, voir App.tsx/AppShell.tsx) : un seul menu, une seule source
  // de vérité pour les filtres pôle/onglet, quel que soit l'affichage choisi.
  // Retour d'une fiche ou de l'assistant ouvert depuis le calendrier en cartes (« Mes demandes », gestion) : on rouvre le Calendrier, sur le même mois
  // (billet lu une fois, puis effacé : rien n'est mémorisé durablement).
  // Jamais appliqué à un lien profond (« Voir les demandes de vos collègues », recherche d'une personne…) ; périmé après dix minutes ; donné une seule fois.
  const [retourCalendrier, setRetourCalendrier] = useState(() => (params?.vue || params?.demande || params?.q ? null : lireRetourCalendrier()));
  useEffect(() => { effacerRetourCalendrier(); }, []);
  const [vueAffichage, setVueAffichage] = useState<'liste' | 'calendrier'>(retourCalendrier ? 'calendrier' : 'liste');
  const [demandes, setDemandes] = useState<Demande[]>([]);
  // À quelle vue (« mes demandes » / « collègues ») appartiennent les demandes chargées, et combien de fois la
  // liste a été rechargée : la liste de l'autre onglet ne doit jamais s'afficher (ni être proposée à « Je me
  // propose ») pendant un changement d'onglet ou de filtre.
  const [vueDesDemandes, setVueDesDemandes] = useState<Filtres['vue'] | null>(null);
  const vueDesDemandesRef = useRef<Filtres['vue'] | null>(null);   // même valeur, lisible dans les rappels asynchrones
  const [versionListe, setVersionListe] = useState(0);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [tri, setTri] = useState<{ col: ColTri; dir: 'asc' | 'desc' }>({ col: 'date', dir: 'asc' });
  const [selection, setSelection] = useState<Set<number>>(new Set());
  const [actionEnCours, setActionEnCours] = useState<Action | 'nettoyage' | 'suppression' | 'restauration' | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  // Confirmation avant suppression : nbEnCours > 0 = au moins une demande
  // sélectionnée n'est pas encore passée (séance future/aujourd'hui) — ton
  // plus appuyé dans la modale que pour un lot 100% de demandes passées.
  const [confirmSuppression, setConfirmSuppression] = useState<{ ids: number[]; total: number; nbEnCours: number } | null>(null);
  // Récapitulatif avant une action en masse (jusqu'à 500 demandes) ou le nettoyage des
  // brouillons périmés : on dit COMBIEN de demandes et QUELLE conséquence, avant d'agir.
  const [confirmGroupee, setConfirmGroupee] = useState<
    { kind: 'action'; action: Action; label: string; ids: number[]; nonConcernees: number; danger: boolean }
    | { kind: 'nettoyage'; n: number }
    | null
  >(null);

  // Seuils d'urgence (jours avant la séance) — mêmes réglages que le
  // Calendrier (calendrier.urgence_*), pour une colonne « Urgence » cohérente.
  const [seuils, setSeuils] = useState<SeuilsUrgence>(SEUILS_URGENCE_PAR_DEFAUT);
  // Périmètre de l'onglet « Demandes des collègues », réglé par l'admin
  // (app_settings.tableau.scope_collegues) — 'off' masque l'onglet plutôt que
  // d'afficher une liste toujours vide. Défaut 'off' avant chargement du
  // réglage réel : évite un flash de l'onglet qui disparaîtrait aussitôt si
  // l'admin l'a désactivé (l'onglet apparaît un instant plus tard si le
  // scope réel est actif — préférable à l'inverse). Avec un lien profond vers
  // l'onglet, on part de 'tous' (le défaut) pour ne pas le faire clignoter.
  const [scopeCollegues, setScopeCollegues] = useState<'tous' | 'pole' | 'pole_et_historique' | 'off'>(lienCollegues ? 'tous' : 'off');
  const [reglagesCharges, setReglagesCharges] = useState(false);
  // Battement de la détection Tipee (minutes) : une séance n'est « à couvrir » que si l'absence la recoupe, battement compris.
  const [battementMin, setBattementMin] = useState(60);
  useEffect(() => {
    api.getSettings().then((s) => {
      setSeuils(seuilsDepuisSettings(s));
      const battement = Number(s['tipee.battement_minutes']);
      setBattementMin(s['tipee.battement_minutes'] != null && Number.isFinite(battement) ? Math.max(0, battement) : 60);
      const scope = s['tableau.scope_collegues'];
      // Réglage absent (base ancienne) : même défaut que le serveur.
      setScopeCollegues(scope === 'pole' || scope === 'pole_et_historique' || scope === 'off' ? scope : 'tous');
    }).catch(() => {}).finally(() => setReglagesCharges(true));
  }, []);
  const maPersonne = useMaPersonne([]);
  // Les comptes externes n'ont jamais accès aux demandes des collègues (le serveur ne leur en renvoie aucune) :
  // l'onglet leur reste fermé plutôt que d'afficher une liste toujours vide.
  const estExterne = maPersonne?.is_externe === true;
  // « Vos remplacements » se recharge après chaque proposition faite depuis la liste.
  const [majRemplacements, setMajRemplacements] = useState(0);
  const aujourdhui = useMemo(() => new Date(), []);

  // Un admin qui bascule l'aperçu intervenant·e sans quitter cette page ne
  // remonte pas le composant : sans cet effet, le toggle « Mes demandes »
  // resterait figé sur la valeur initiale au lieu de refléter le rôle
  // affiché — l'aperçu ne serait plus fidèle.
  // Au montage l'état initial est déjà le bon (il peut venir d'un lien profond) : on ne
  // resynchronise que sur un vrai changement de rôle.
  const peutGererPrecedent = useRef(peutGerer);
  useEffect(() => {
    if (peutGererPrecedent.current === peutGerer) return;
    peutGererPrecedent.current = peutGerer;
    setFiltres((f) => ({ ...f, miennes: !peutGerer, vue: 'miennes' }));
  }, [peutGerer]);
  // Un nouveau lien Slack alors que la page est déjà ouverte rouvre l'onglet — sauf s'il est fermé à cette
  // personne (désactivé par l'admin, compte externe) : elle reste alors sur « Mes demandes ».
  const ongletCollegues = !peutGerer && !estExterne && !(reglagesCharges && scopeCollegues === 'off');
  useEffect(() => {
    if (lienCollegues && ongletCollegues) setFiltres((f) => (f.vue === 'collegues' ? f : { ...f, vue: 'collegues', corbeille: false, ...FILTRES_VIERGES }));
  }, [lienCollegues, ongletCollegues, epingleId]);
  // Onglet désactivé par l'admin (ou fermé aux externes) : on ne reste pas sur une vue qu'il masque.
  // (filtres.vue en dépendance : si la vue devenait « collègues » malgré tout, on en sort.)
  useEffect(() => {
    if (((reglagesCharges && scopeCollegues === 'off') || estExterne) && filtres.vue === 'collegues') {
      setFiltres((f) => (f.vue === 'collegues' ? { ...f, vue: 'miennes' } : f));
    }
  }, [reglagesCharges, scopeCollegues, estExterne, filtres.vue]);
  // La gestion n'a pas d'onglet « collègues » : le lien Slack la mène droit à la fiche (remplace l'entrée
  // d'historique, sinon « Précédent » reviendrait ici et la renverrait aussitôt à la fiche).
  useEffect(() => {
    if (lienCollegues && peutGerer && epingleId != null) window.location.replace(`#demandes/${epingleId}`);
  }, [lienCollegues, peutGerer, epingleId]);

  // Stats globales — utiles uniquement à la gestion (pas de sens pour un·e
  // intervenant·e, qui ne suit que ses propres demandes). Réutilisée après
  // une action groupée / un nettoyage pour rafraîchir les compteurs.
  const [toutes, setToutes] = useState<Demande[]>([]);
  // Tant que la liste complète n'est pas arrivée, on ne dit rien : « aucune urgence » serait faux.
  const [toutesChargees, setToutesChargees] = useState(false);
  const chargerToutes = useCallback(() => {
    if (!peutGerer) return Promise.resolve();
    return api.getDemandes({}).then((d) => { setToutes(d); setToutesChargees(true); }).catch(() => {});
  }, [peutGerer]);
  useEffect(() => { chargerToutes(); }, [chargerToutes]);

  // Inutile de charger la liste tant qu'on est en vue Calendrier — celle-ci
  // fait son propre chargement (TableauCalendrier), scopé au mois affiché.
  // `silencieux` : recharge sans remplacer la liste par un spinner (après une action faite DANS la
  // liste des collègues — sinon elle clignote et la page saute).
  // Seule la réponse à la DERNIÈRE demande de chargement compte : une réponse plus lente d'un chargement
  // précédent (changement d'onglet ou de filtre, retour de connexion) est ignorée.
  const dernierChargement = useRef(0);
  const chargerDemandes = useCallback((silencieux = false) => {
    if (vueAffichage !== 'liste') return Promise.resolve();
    const numero = ++dernierChargement.current;
    const vueChargee = filtres.vue;
    if (!silencieux) setLoading(true);
    setErreur(null);
    const requete = paramsDepuisFiltres(filtres, !peutGerer);
    // Collègues : séances à venir seulement (une séance passée ne peut plus être remplacée).
    if (filtres.vue === 'collegues') requete.du = jourLocalIso();
    return api.getDemandes(requete)
      .then((d) => {
        if (numero !== dernierChargement.current) return;
        setDemandes(d); vueDesDemandesRef.current = vueChargee; setVueDesDemandes(vueChargee);
        setVersionListe((v) => v + 1); setSelection(new Set());
      })
      .catch((err: unknown) => {
        if (numero !== dernierChargement.current) return;
        setErreur(err instanceof Error ? err.message : 'Impossible de charger les demandes.');
        // Les demandes affichées ne sont plus celles de cette vue (autre onglet) ou de ces filtres (rechargement non
        // silencieux) : on les vide (état vide + message d'erreur) plutôt qu'une roue qui tourne sans fin.
        // Seule une liste déjà bonne ET rechargée en silence (après une action) reste en place.
        if (!silencieux || vueDesDemandesRef.current !== vueChargee) {
          setDemandes([]); vueDesDemandesRef.current = vueChargee; setVueDesDemandes(vueChargee);
        }
      })
      .finally(() => { if (numero === dernierChargement.current) setLoading(false); });
  }, [filtres, vueAffichage, peutGerer]);
  // Chargement en cours, ou demandes encore celles de l'autre onglet.
  const enChargement = loading || vueDesDemandes !== filtres.vue;
  useEffect(() => { void chargerDemandes(); }, [chargerDemandes]);
  // Après une action (qui peut durer), on recharge avec les filtres D'AUJOURD'HUI, pas ceux du rendu où la
  // fonction a été créée : sinon l'ancienne requête passait pour « la dernière » et la liste des anciens filtres
  // s'affichait sous les nouveaux.
  const chargerRef = useRef(chargerDemandes);
  useEffect(() => { chargerRef.current = chargerDemandes; });
  const rechargerListe = useCallback((silencieux = false) => chargerRef.current(silencieux), []);
  const rechargerApresAction = useCallback(() => {
    void chargerRef.current(true);
    setMajRemplacements((n) => n + 1);
  }, []);

  const nettoyerBrouillonsPerimes = async () => {
    setActionEnCours('nettoyage');
    setActionMsg(null);
    try {
      const res = await api.nettoyerBrouillonsPerimes();
      setActionMsg(res.ecartees > 0
        ? `${res.ecartees} brouillon${res.ecartees > 1 ? 's périmés écartés' : ' périmé écarté'}`
        : 'Aucun brouillon périmé à écarter');
      await Promise.all([rechargerListe(), chargerToutes()]);
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : 'Échec du nettoyage');
    } finally {
      setActionEnCours(null);
    }
  };

  function demanderSuppression(ids: number[]) {
    if (ids.length === 0) return;
    const items = demandes.filter((d) => ids.includes(d.id));
    const nbEnCours = items.filter((d) => joursAvant(d.date_atelier, aujourdhui) >= 0).length;
    setConfirmSuppression({ ids, total: items.length, nbEnCours });
  }

  async function confirmerSuppression() {
    if (!confirmSuppression) return;
    const { ids } = confirmSuppression;
    setConfirmSuppression(null);
    setActionEnCours('suppression');
    setActionMsg(null);
    try {
      const res = await api.supprimerDemandes(ids);
      setActionMsg(
        `${res.reussies} demande${res.reussies > 1 ? 's supprimées' : ' supprimée'}`
        + (res.echouees.length > 0 ? ` · ${res.echouees.length} échec${res.echouees.length > 1 ? 's' : ''}` : '')
        + ' — récupérable depuis la Corbeille.',
      );
      await Promise.all([rechargerListe(), chargerToutes()]);
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : 'Échec de la suppression');
    } finally {
      setActionEnCours(null);
    }
  }

  const restaurerSelection = async () => {
    const ids = Array.from(selection);
    if (ids.length === 0) return;
    setActionEnCours('restauration');
    setActionMsg(null);
    try {
      const res = await api.restaurerDemandes(ids);
      setActionMsg(
        `${res.reussies} demande${res.reussies > 1 ? 's restaurées' : ' restaurée'}`
        + (res.echouees.length > 0 ? ` · ${res.echouees.length} échec${res.echouees.length > 1 ? 's' : ''}` : ''),
      );
      await Promise.all([rechargerListe(), chargerToutes()]);
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : 'Échec de la restauration');
    } finally {
      setActionEnCours(null);
    }
  };

  // Demandes réellement sélectionnées (objets, pas juste des ids) — sert à
  // savoir, pour chaque action groupée, laquelle des lignes cochées l'accepte
  // réellement (demande.actions_possibles, calculée par la même machine à
  // états que la fiche demande — pas de règle dupliquée côté client).
  const demandesSelectionnees = useMemo(
    () => demandes.filter((d) => selection.has(d.id)),
    [demandes, selection],
  );
  const idsCompatibles = (action: Action) =>
    demandesSelectionnees.filter((d) => (d.actions_possibles ?? []).includes(action)).map((d) => d.id);

  const demanderActionGroupee = (action: Action) => {
    const ids = idsCompatibles(action);
    if (ids.length === 0) return;
    const a = ACTIONS_GROUPEES.find((x) => x.action === action);
    setConfirmGroupee({ kind: 'action', action, label: a?.label ?? String(action), ids, nonConcernees: selection.size - ids.length, danger: !!a?.danger });
  };

  const lancerActionGroupee = async (action: Action) => {
    const ids = idsCompatibles(action);
    if (ids.length === 0) return;
    setActionEnCours(action);
    setActionMsg(null);
    try {
      const res = await api.appliquerActionGroupee(ids, action);
      const detail = res.echouees.slice(0, 3).map((e) => `#${e.id} — ${e.erreur}`).join(' · ');
      setActionMsg(
        `${res.reussies} demande${res.reussies > 1 ? 's mises' : ' mise'} à jour`
        + (res.echouees.length > 0
          ? ` · ${res.echouees.length} échec${res.echouees.length > 1 ? 's' : ''} : ${detail}${res.echouees.length > 3 ? '…' : ''}`
          : ''),
      );
      await Promise.all([rechargerListe(), chargerToutes()]);
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : "Échec de l'action groupée");
    } finally {
      setActionEnCours(null);
    }
  };

  const basculerSelection = (id: number) => {
    setSelection((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Filtres rapides déclenchés par les cartes-stats : chaque preset écrase
  // statut/echeanceDepassee/urgenceActive/actives/mois, mais garde pole_id et
  // miennes tels quels. `actives` repasse à false car les compteurs sources
  // (`toutes`) ne l'appliquent pas — sinon le clic sur une carte incluant un
  // statut terminal (ex. clôturées) renverrait toujours 0 résultat.
  const filtrerPreset = (preset: Partial<Filtres>) =>
    setFiltres((f) => ({ ...f, statuts: [], echeanceDepassee: false, urgenceActive: false, actives: false, mois: '', ...preset }));

  const maintenant = Date.now();
  const debutMois = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const finMois = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1);

  // Comptages alignés sur META_ETAT_STATUTS (source unique, format.ts) — pas
  // de liste de statuts dupliquée ici, sinon elle diverge du regroupement
  // réel dès qu'un statut change de méta-état.
  const aTraiter = toutes.filter((d) => META_ETAT_STATUTS.a_traiter.includes(d.statut)).length;
  const rechercheEnCours = toutes.filter((d) => META_ETAT_STATUTS.recherche.includes(d.statut)).length;
  const enPassation = toutes.filter((d) => META_ETAT_STATUTS.passation.includes(d.statut)).length;
  const echeancesDepassees = toutes.filter((d) => d.prochaine_echeance && new Date(d.prochaine_echeance).getTime() < maintenant).length;
  const termineesCeMois = toutes.filter((d) => META_ETAT_STATUTS.termine.includes(d.statut) && new Date(d.updated_at) >= debutMois && new Date(d.updated_at) < finMois).length;
  const urgencesActives = toutes.filter((d) =>
    (d.type === 'urgence_weekend' || d.type === 'urgence_semaine' || d.statut === 'urgence_cascade') &&
    !STATUTS_TERMINAUX.includes(d.statut)
  ).length;

  // Séances à couvrir d'urgence : dans les N prochains jours (seuil « Urgent » réglable), sans remplaçant·e.
  const seancesUrgentes = useMemo(() => seancesACouvrir(toutes, aujourdhui, seuils.urgent), [toutes, aujourdhui, seuils.urgent]);

  // Surligne la carte-stat qui correspond exactement au méta-état filtré —
  // même fonction que le <select>, pour ne jamais diverger de ce qu'il affiche.
  const metaEtatActif = metaEtatDeStatuts(filtres.statuts);
  const presetActif = {
    aTraiter: metaEtatActif === 'a_traiter' && !filtres.echeanceDepassee && !filtres.urgenceActive,
    recherche: metaEtatActif === 'recherche' && !filtres.echeanceDepassee && !filtres.urgenceActive,
    passation: metaEtatActif === 'passation' && !filtres.echeanceDepassee && !filtres.urgenceActive,
    echeances: filtres.echeanceDepassee,
    terminees: metaEtatActif === 'termine' && filtres.mois === moisCourantStr(),
    urgences: filtres.urgenceActive,
  };

  const toggleTri = (col: ColTri) =>
    setTri((t) => t.col === col ? { col, dir: t.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });

  // Urgence par demande — uniquement pour les statuts non résolus (une
  // demande clôturée/refusée n'a plus d'échéance qui compte).
  const urgenceParDemande = useMemo(() => {
    const m = new Map<number, { joursRestants: number; niveau: ReturnType<typeof niveauUrgence> }>();
    demandes.forEach((d) => {
      // Une urgence en cours n'est pas réglée non plus : sans ça, la colonne affichait « — » pour ce qui presse le plus.
      if (!STATUTS_NON_RESOLUS.includes(d.statut) && d.statut !== 'urgence_cascade') return;
      const joursRestants = joursAvant(d.date_atelier, aujourdhui);
      m.set(d.id, { joursRestants, niveau: niveauUrgence(joursRestants, seuils) });
    });
    return m;
  }, [demandes, aujourdhui, seuils]);

  const demandesTriees = useMemo(() => [...demandes].sort((a, b) => {
    if (tri.col === 'urgence') {
      const ja = urgenceParDemande.get(a.id)?.joursRestants ?? Infinity;
      const jb = urgenceParDemande.get(b.id)?.joursRestants ?? Infinity;
      return tri.dir === 'asc' ? ja - jb : jb - ja;
    }
    let va: string, vb: string;
    if (tri.col === 'date')         { va = a.date_atelier;               vb = b.date_atelier; }
    else if (tri.col === 'atelier') { va = a.atelier_nom ?? '';           vb = b.atelier_nom ?? ''; }
    else if (tri.col === 'absent')  { va = a.absent_nom ?? '';            vb = b.absent_nom ?? ''; }
    else if (tri.col === 'remplacant') { va = a.remplacant_nom ?? '';     vb = b.remplacant_nom ?? ''; }
    else if (tri.col === 'statut')  { va = a.statut;                      vb = b.statut; }
    else                            { va = a.prochaine_echeance ?? '9999-99-99'; vb = b.prochaine_echeance ?? '9999-99-99'; }
    const cmp = va.localeCompare(vb, 'fr');
    return tri.dir === 'asc' ? cmp : -cmp;
  }), [demandes, tri, urgenceParDemande]);

  // Filtre texte final, appliqué après le tri — sur atelier, absent·e,
  // remplaçant·e et pôle : les champs déjà visibles dans les colonnes du
  // tableau, pour que ce qui matche soit toujours explicable au premier coup d'œil.
  const qNorm = q.trim().toLowerCase();
  // Origine : compteurs calculés sur la liste déjà chargée (avant le filtre
  // d'origine lui-même) pour que chaque choix annonce ce qu'il contiendra.
  const nbTipee = useMemo(() => demandesTriees.filter((d) => d.source === 'tipee').length, [demandesTriees]);
  const nbMain = demandesTriees.length - nbTipee;
  const demandesAffichees = useMemo(
    () => demandesTriees
      .filter((d) => filtres.origine === 'toutes' || (filtres.origine === 'tipee') === (d.source === 'tipee'))
      .filter((d) => !qNorm || [d.atelier_nom, d.atelier_lieu, d.absent_nom, d.remplacant_nom, d.pole_nom].some((v) => v?.toLowerCase().includes(qNorm))),
    [demandesTriees, qNorm, filtres.origine],
  );

  // Vue intervenant·e : les brouillons repérés dans Tipee sont présentés par
  // période d'absence (un bloc, un seul geste « Envoyer mes N demandes ») et
  // retirés du tableau pour ne pas apparaître deux fois.
  const groupesTipee = useMemo(() => {
    if (peutGerer || filtres.vue === 'collegues' || filtres.corbeille) return [] as Demande[][];
    const parPeriode = new Map<string, Demande[]>();
    for (const d of demandesAffichees) {
      if (d.source !== 'tipee' || d.statut !== 'brouillon' || !d.absence_debut) continue;
      const cle = `${d.absent_personne_id}|${d.absence_debut}`;
      const l = parPeriode.get(cle) ?? [];
      l.push(d);
      parPeriode.set(cle, l);
    }
    return [...parPeriode.values()];
  }, [demandesAffichees, peutGerer, filtres.vue, filtres.corbeille]);
  const idsGroupes = useMemo(() => new Set(groupesTipee.flat().map((d) => d.id)), [groupesTipee]);
  const lignesTableau = useMemo(() => demandesAffichees.filter((d) => !idsGroupes.has(d.id)), [demandesAffichees, idsGroupes]);

  const exporterCSV = () => {
    const lignes = [
      ['Date', 'Atelier', 'Pôle', 'Absent', 'Remplaçant', 'Statut', 'Échéance'],
      ...demandesAffichees.map((d) => [
        d.date_atelier,
        d.atelier_nom ?? '',
        d.pole_nom ?? '',
        d.absent_nom ?? '',
        d.remplacant_nom ?? '',
        meta?.statuts[d.statut] ?? d.statut,
        d.prochaine_echeance ?? '',
      ]),
    ];
    const csv = lignes.map((l) => l.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `demandes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Onglet collègues : les demandes masquées ne comptent pas (elles sont repliées dans la liste).
  const nbListe = filtres.vue === 'collegues' ? demandesAffichees.filter((d) => !d.skippee_par_moi).length : demandesAffichees.length;

  // Changer d'onglet quitte aussi la demande épinglée : le lien Slack n'a plus lieu d'être dans l'adresse.
  const choisirVue = (vue: Filtres['vue']) => {
    if (vue === filtres.vue) return;
    setFiltres((f) => ({ ...f, vue, corbeille: false, ...FILTRES_VIERGES }));
    if (params?.vue || params?.demande) window.location.replace('#tableau');
  };

  const fleche = (col: ColTri) => tri.col === col ? (tri.dir === 'asc' ? ' ↑' : ' ↓') : ' ↕';

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Tableau de bord</h1>
          {/* Toujours rendu (même hauteur dans les deux vues) — un texte qui
              apparaît/disparaît ici décalerait tout ce qui suit d'une ligne
              au moment précis où on vient de cliquer Liste/Calendrier. */}
          <p className="page-meta">
            {vueAffichage === 'liste' ? `${nbListe} demande${nbListe > 1 ? 's' : ''}` : ' '}
          </p>
        </div>
        <div className="flex gap-8">
          {peutGerer && vueAffichage === 'liste' && (
            <button
              className="btn"
              onClick={() => {
                const aujourdhui = new Date().toISOString().slice(0, 10);
                const n = toutes.filter((d) => d.statut === 'brouillon' && d.date_atelier.slice(0, 10) < aujourdhui).length;
                if (n === 0) { setActionMsg('Aucun brouillon périmé à écarter'); return; }
                setConfirmGroupee({ kind: 'nettoyage', n });
              }}
              disabled={actionEnCours === 'nettoyage'}
              title="Écarte automatiquement les brouillons dont la séance est déjà passée"
            >
              <Sparkles className="icon-sm" /> {actionEnCours === 'nettoyage' ? 'Nettoyage…' : 'Nettoyer les brouillons périmés'}
            </button>
          )}
          {peutGerer && vueAffichage === 'liste' && (
            <button className="btn" onClick={exporterCSV} title="Exporter la liste filtrée en CSV">
              <Download className="icon-sm" /> Exporter
            </button>
          )}
          {/* Pas dans l'onglet « Demandes des collègues » : cet écran sert à aider
              les autres, pas à déclarer sa propre absence (une action principale
              par écran, cohérente avec son but). */}
          {filtres.vue !== 'collegues' && (
            <button className="btn btn-accent" onClick={() => naviguer('nouvelle')}>
              <Plus className="icon-sm" /> Nouvelle demande
            </button>
          )}
        </div>
      </div>

      {/* Bascule Liste/Calendrier — sur sa propre ligne, dont le contenu ne
          varie JAMAIS (toujours ces deux boutons, rien d'autre) : le bouton
          ne doit jamais se déplacer au clic, contrairement à s'il partageait
          une barre dont d'autres éléments apparaissent/disparaissent selon
          la vue active. Voir feedback-ux-first. */}
      <div className="flex justify-end mb-16">
        <div className="selectable-group">
          <button type="button" className={`selectable ${vueAffichage === 'liste' ? 'active' : ''}`} onClick={() => setVueAffichage('liste')}>Liste</button>
          <button type="button" className={`selectable ${vueAffichage === 'calendrier' ? 'active' : ''}`} onClick={() => setVueAffichage('calendrier')}>Calendrier</button>
        </div>
      </div>

      {actionMsg && <div className="badge badge-accent mb-16" style={{ display: 'block', padding: '8px 12px', textTransform: 'none', letterSpacing: 'normal', fontSize: 'var(--t-13)' }}>{actionMsg}</div>}

      {/* « Vos remplacements » : séances où la personne est désignée remplaçant·e
          (à confirmer / à venir), AU-DESSUS des onglets, visible en vue Liste ET
          Calendrier. N'existe pas tant qu'il n'y a rien à montrer. */}
      <VosRemplacements rechargerCle={`${filtres.vue}-${vueAffichage}-${majRemplacements}`} onChange={() => { void rechargerListe(true); }} />

      {!peutGerer && scopeCollegues !== 'off' && !estExterne && (
        <div className="tabs mb-16" role="tablist" aria-label="Vue du tableau de bord">
          <div
            role="tab" tabIndex={0} aria-selected={filtres.vue === 'miennes'}
            className={`tab ${filtres.vue === 'miennes' ? 'active' : ''}`}
            onClick={() => choisirVue('miennes')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choisirVue('miennes'); }
              else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); choisirVue('collegues'); }
            }}
          >
            Mes demandes
          </div>
          <div
            role="tab" tabIndex={0} aria-selected={filtres.vue === 'collegues'}
            className={`tab ${filtres.vue === 'collegues' ? 'active' : ''}`}
            onClick={() => choisirVue('collegues')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choisirVue('collegues'); }
              else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); choisirVue('miennes'); }
            }}
          >
            Demandes des collègues
          </div>
        </div>
      )}

      {peutGerer && toutesChargees && (
        <BandeauUrgences seances={seancesUrgentes} jours={seuils.urgent} aujourdhui={aujourdhui} onOuvrir={(id) => naviguer('demandes', id)} />
      )}

      {peutGerer && (
        <div className="stats-grid mb-24">
          <StatCarte valeur={aTraiter} label="À traiter" active={presetActif.aTraiter} onClick={() => filtrerPreset({ statuts: [...META_ETAT_STATUTS.a_traiter] })} />
          <StatCarte valeur={rechercheEnCours} label="Recherche en cours" active={presetActif.recherche} onClick={() => filtrerPreset({ statuts: [...META_ETAT_STATUTS.recherche] })} />
          <StatCarte valeur={enPassation} label="En passation" active={presetActif.passation} onClick={() => filtrerPreset({ statuts: [...META_ETAT_STATUTS.passation] })} />
          <StatCarte valeur={echeancesDepassees} label="Échéances dépassées" active={presetActif.echeances} onClick={() => filtrerPreset({ echeanceDepassee: true })} />
          <StatCarte valeur={termineesCeMois} label="Terminées ce mois" active={presetActif.terminees} onClick={() => filtrerPreset({ statuts: [...META_ETAT_STATUTS.termine], mois: moisCourantStr() })} />
          <StatCarte
            valeur={urgencesActives} label="Procédures d'urgence" active={presetActif.urgences}
            valeurStyle={urgencesActives > 0 ? { color: 'var(--err)' } : undefined}
            onClick={() => filtrerPreset({ urgenceActive: true })}
          />
        </div>
      )}

      <div className="toolbar">
        <div className="filter-chips">
          <button
            className={`chip ${filtres.pole_id === '' ? 'active' : ''}`}
            onClick={() => setFiltres((f) => ({ ...f, pole_id: '' }))}
          >
            Tous les pôles
          </button>
          {(meta?.poles ?? []).map((pole) => (
            <button
              key={pole.id}
              className={`chip inline-flex items-center gap-4 ${filtres.pole_id === pole.id ? 'active' : ''}`}
              onClick={() => setFiltres((f) => ({ ...f, pole_id: f.pole_id === pole.id ? '' : pole.id }))}
            >
              <PoleDot couleur={pole.couleur} /> {pole.nom}
            </button>
          ))}
        </div>

        {vueAffichage === 'liste' && (
          <div className="search-wrapper" style={{ maxWidth: 260 }}>
            <input
              className="search-input"
              placeholder="Atelier ou personne…" aria-label="Rechercher un atelier ou une personne"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        )}

        {vueAffichage === 'liste' && filtres.vue !== 'collegues' && (
          <select
            className="form-select"
            style={{ width: 'auto' }}
            aria-label="Filtrer par état"
            value={metaEtatDeStatuts(filtres.statuts)}
            onChange={(e) => setFiltres((f) => ({
              ...f,
              statuts: e.target.value ? [...META_ETAT_STATUTS[e.target.value as MetaEtat]] : [],
              echeanceDepassee: false,
              urgenceActive: false,
            }))}
          >
            <option value="">Tous les états</option>
            {META_ETATS_ORDRE.map((m) => (
              <option key={m} value={m}>{META_ETAT_LABEL[m]}</option>
            ))}
          </select>
        )}

        {/* Origine : réservé à la gestion (l'intervenant·e ne voit que ses
            propres demandes, le badge suffit). N'apparaît que s'il y a des
            demandes repérées dans Tipee à trier — sinon ce serait du superflu. */}
        {vueAffichage === 'liste' && peutGerer && filtres.vue !== 'collegues' && (nbTipee > 0 || filtres.origine !== 'toutes') && (
          <div className="flex items-center gap-8" role="group" aria-label="Origine des demandes">
            <span className="text-xs text-ink-3" style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Origine</span>
            <div className="selectable-group">
              {([
                ['toutes', 'Toutes', demandesTriees.length],
                ['main', 'Saisies à la main', nbMain],
                ['tipee', 'Repérées dans Tipee', nbTipee],
              ] as const).map(([val, label, n]) => (
                <button
                  key={val}
                  type="button"
                  className={`selectable ${filtres.origine === val ? 'active' : ''}`}
                  aria-pressed={filtres.origine === val}
                  onClick={() => setFiltres((f) => ({ ...f, origine: val }))}
                >
                  {label} <span className="text-xs" style={{ opacity: 0.75 }}>{n}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {vueAffichage === 'liste' && peutGerer && (
          <label className="toggle">
            <input
              type="checkbox"
              className="toggle-input"
              checked={filtres.miennes}
              onChange={(e) => setFiltres((f) => ({ ...f, miennes: e.target.checked }))}
            />
            <span className="toggle-slider" />
            <span className="toggle-label">Mes demandes</span>
          </label>
        )}

        {vueAffichage === 'liste' && filtres.vue !== 'collegues' && (
          <label className="toggle">
            <input
              type="checkbox"
              className="toggle-input"
              checked={filtres.actives}
              onChange={(e) => setFiltres((f) => ({ ...f, actives: e.target.checked }))}
            />
            <span className="toggle-slider" />
            <span className="toggle-label">Actives seulement</span>
          </label>
        )}

        {vueAffichage === 'liste' && filtres.vue !== 'collegues' && (
          <label className="toggle" title={peutGerer ? 'Demandes supprimées (toutes)' : 'Vos demandes supprimées'}>
            <input
              type="checkbox"
              className="toggle-input"
              checked={filtres.corbeille}
              onChange={(e) => setFiltres((f) => ({ ...f, corbeille: e.target.checked }))}
            />
            <span className="toggle-slider" />
            <span className="toggle-label">Corbeille</span>
          </label>
        )}
      </div>

      {erreur && <div className="badge badge-err mb-16" style={{ display: 'block', padding: '8px 12px', textTransform: 'none', letterSpacing: 'normal', fontSize: 'var(--t-13)' }}>{erreur}</div>}

      {vueAffichage === 'calendrier' ? (
        <TableauCalendrier peutGerer={peutGerer} vue={filtres.vue} poleFiltre={filtres.pole_id} meta={meta} seuils={seuils} battementMin={battementMin} retour={retourCalendrier} onRetourApplique={() => setRetourCalendrier(null)} onChange={() => setMajRemplacements((n) => n + 1)} />
      ) : (
      <>
      {/* Toujours rendue quand la sélection est possible (même principe que
          page-meta plus haut) — masquer/afficher cette barre selon
          selection.size décalerait tout le tableau d'une ligne à chaque
          coche/décoche, ce qui déplace les lignes sous le curseur au pire
          moment (juste après avoir cliqué une case). Seul le CONTENU change. */}
      {filtres.vue !== 'collegues' && (
        <div className="toolbar mb-12" style={{ background: selection.size > 0 ? 'var(--accent-dim)' : 'var(--bg-inset)', borderRadius: 'var(--r1)', padding: '8px 12px' }}>
          {selection.size > 0 ? (
            <>
              <span className="text-sm font-medium">{selection.size} sélectionnée{selection.size > 1 ? 's' : ''}</span>
              <div className="flex gap-8 ml-8">
                {peutGerer && ACTIONS_GROUPEES.map((a) => {
                  const ids = idsCompatibles(a.action);
                  return (
                    <button
                      key={a.action}
                      className={`btn btn-sm ${a.danger ? 'btn-danger' : ''}`}
                      disabled={actionEnCours != null || ids.length === 0}
                      title={ids.length === 0 ? "Aucune des demandes sélectionnées n'accepte cette action depuis son statut actuel" : undefined}
                      onClick={() => demanderActionGroupee(a.action)}
                    >
                      {actionEnCours === a.action ? 'En cours…' : `${a.label} (${ids.length}/${selection.size})`}
                    </button>
                  );
                })}
                {filtres.corbeille ? (
                  <button
                    className="btn btn-sm btn-accent"
                    disabled={actionEnCours != null}
                    onClick={restaurerSelection}
                  >
                    {actionEnCours === 'restauration' ? 'Restauration…' : 'Restaurer'}
                  </button>
                ) : (
                  <button
                    className="btn btn-sm btn-danger"
                    disabled={actionEnCours != null}
                    onClick={() => demanderSuppression(Array.from(selection))}
                  >
                    Supprimer
                  </button>
                )}
              </div>
              <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setSelection(new Set())}>
                Tout désélectionner
              </button>
            </>
          ) : (
            <span className="text-sm text-ink-3">Cochez une ou plusieurs demandes pour agir en lot.</span>
          )}
        </div>
      )}

      {!enChargement && <GroupesAbsenceTipee groupes={groupesTipee} onChange={() => { void rechargerListe(); }} />}

      {filtres.vue === 'collegues' ? (
        <DemandesCollegues
          demandes={demandesAffichees} versionListe={versionListe} chargement={enChargement} maPersonneId={maPersonne?.id ?? null}
          epingleId={epingleId} filtreActif={filtres.pole_id !== '' || qNorm !== ''}
          scope={scopeCollegues === 'off' ? 'tous' : scopeCollegues} onChange={rechargerApresAction}
        />
      ) : enChargement ? (
        <div className="flex justify-center p-24"><div className="spinner" /></div>
      ) : lignesTableau.length === 0 && groupesTipee.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><Inbox /></div>
          <div className="empty-state-title">Aucune demande</div>
          <div className="empty-state-desc">
            {qNorm && demandes.length > 0
              ? `Aucun résultat pour « ${q.trim()} ».`
              : 'Aucune demande ne correspond à ces filtres.'}
          </div>
        </div>
      ) : lignesTableau.length === 0 ? null : (
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input
                  type="checkbox"
                  aria-label="Tout sélectionner"
                  checked={selection.size > 0 && selection.size === lignesTableau.length}
                  ref={(el) => { if (el) el.indeterminate = selection.size > 0 && selection.size < lignesTableau.length; }}
                  onChange={(e) => setSelection(e.target.checked ? new Set(lignesTableau.map((d) => d.id)) : new Set())}
                />
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => toggleTri('urgence')}>Urgence{fleche('urgence')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => toggleTri('date')}>Date{fleche('date')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => toggleTri('atelier')}>Atelier{fleche('atelier')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => toggleTri('absent')}>Absent·e{fleche('absent')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => toggleTri('remplacant')}>Remplaçant·e{fleche('remplacant')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => toggleTri('statut')}>Statut{fleche('statut')}</th>
              {filtres.corbeille ? <th>Supprimée</th> : <th style={{ cursor: 'pointer' }} onClick={() => toggleTri('echeance')}>Échéance{fleche('echeance')}</th>}
            </tr>
          </thead>
          <tbody>
            {lignesTableau.map((d) => {
              const nonResolue = STATUTS_NON_RESOLUS.includes(d.statut);
              const urgence = urgenceParDemande.get(d.id);
              return (
                <tr
                  key={d.id}
                  className={`cursor-pointer ${nonResolue ? 'demande-non-resolue' : ''}`}
                  onClick={() => naviguer('demandes', d.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); naviguer('demandes', d.id); } }}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Sélectionner la demande du ${formatDateCourt(d.date_atelier)}`}
                      checked={selection.has(d.id)}
                      onChange={() => basculerSelection(d.id)}
                    />
                  </td>
                  <td>
                    {urgence ? (
                      <>
                        <span className={NIVEAU_URGENCE_INFO[urgence.niveau].badgeClass}>{NIVEAU_URGENCE_INFO[urgence.niveau].label}</span>
                        <span className="text-xs text-muted ml-8 tabular-nums">{libelleEcheance(urgence.joursRestants)}</span>
                      </>
                    ) : '—'}
                  </td>
                  <td className="tabular-nums">{formatDateCourt(d.date_atelier)}</td>
                  <td>
                    <span className="font-medium text-ink-1">
                      <PoleDot couleur={d.pole_couleur} /> {d.atelier_nom}
                    </span>
                    {d.atelier_lieu && <div className="text-xs text-ink-3">{d.atelier_lieu}</div>}
                    {d.type !== 'planifie' && <div className="mt-4"><TypeBadge type={d.type} /></div>}
                  </td>
                  <td>{d.absent_nom}</td>
                  <td>{(peutGerer ? d.remplacant_nom : remplacantEnVigueur(d)) || '—'}</td>
                  <td>
                    {peutGerer ? (
                      // Gestion : le statut précis reste (c'est son vocabulaire), et dessous la même mini-frise que les intervenant·e·s —
                      // où en est la demande, et qui tient la balle (« menée par Alexia », « suivi par l'équipe gestion »…). DECISIONS §27.
                      <>
                        <StatutBadge statut={d.statut} libelle={meta?.statuts[d.statut]} />
                        <div className="mt-4"><MiniFrise demande={d} peutGerer /></div>
                      </>
                    ) : (
                      // Vue intervenant·e : où en est la demande (mini-frise + une phrase) ; le statut précis reste en infobulle.
                      <>
                        <StatutBadge statut={d.statut} libelle={meta?.statuts[d.statut]} compact sansInfobulle />
                        <div className="mt-4"><MiniFrise demande={d} peutGerer={false} /></div>
                      </>
                    )}
                    {d.source === 'tipee' && <div className="mt-4"><OrigineBadge source={d.source} /></div>}
                  </td>
                  {filtres.corbeille ? (
                    <td className="text-xs text-ink-3">
                      {d.deleted_at ? formatDateCourt(d.deleted_at) : '—'}{d.supprime_par_nom ? ` · ${d.supprime_par_nom}` : ''}
                    </td>
                  ) : (
                    <td className={classeEcheance(d.prochaine_echeance)}>
                      {d.prochaine_echeance ? formatDateCourt(d.prochaine_echeance) : '—'}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      </>
      )}

      {vueAffichage === 'liste' && confirmGroupee && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {confirmGroupee.kind === 'nettoyage'
                  ? `Écarter ${confirmGroupee.n} brouillon${confirmGroupee.n > 1 ? 's périmés' : ' périmé'} ?`
                  : `${confirmGroupee.label} : ${confirmGroupee.ids.length} demande${confirmGroupee.ids.length > 1 ? 's' : ''} ?`}
              </h3>
              <button type="button" className="close-btn" onClick={() => setConfirmGroupee(null)} aria-label="Fermer">✕</button>
            </div>
            <div className="modal-body">
              <p className="text-sm text-ink-2">
                {confirmGroupee.kind === 'nettoyage'
                  ? "Ces brouillons dont la date d'atelier est passée seront classés sans suite. Action définitive."
                  : DESCRIPTIONS_GROUPEES[confirmGroupee.action] ?? 'Cette action sera appliquée à chaque demande.'}
              </p>
              {confirmGroupee.kind === 'action' && confirmGroupee.nonConcernees > 0 && (
                <p className="text-xs text-ink-3" style={{ marginTop: 8 }}>
                  {confirmGroupee.nonConcernees} demande{confirmGroupee.nonConcernees > 1 ? 's' : ''} sélectionnée{confirmGroupee.nonConcernees > 1 ? 's ne sont' : ' n\'est'} pas concernée{confirmGroupee.nonConcernees > 1 ? 's' : ''} (statut incompatible) et {confirmGroupee.nonConcernees > 1 ? 'resteront' : 'restera'} inchangée{confirmGroupee.nonConcernees > 1 ? 's' : ''}.
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn" onClick={() => setConfirmGroupee(null)}>Retour</button>
              <button
                type="button"
                className={`btn ${confirmGroupee.kind === 'nettoyage' || confirmGroupee.danger ? 'btn-danger' : 'btn-accent'}`}
                onClick={() => {
                  const c = confirmGroupee;
                  setConfirmGroupee(null);
                  if (c.kind === 'nettoyage') void nettoyerBrouillonsPerimes();
                  else void lancerActionGroupee(c.action);
                }}
              >
                {confirmGroupee.kind === 'nettoyage' ? `Oui, écarter ${confirmGroupee.n}` : `${confirmGroupee.label} (${confirmGroupee.ids.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {vueAffichage === 'liste' && confirmSuppression && (
        <ModalConfirmerSuppression
          total={confirmSuppression.total}
          nbEnCours={confirmSuppression.nbEnCours}
          onAnnuler={() => setConfirmSuppression(null)}
          onConfirmer={confirmerSuppression}
        />
      )}

    </div>
  );
}

interface ModalConfirmerSuppressionProps {
  total: number;
  nbEnCours: number;
  onAnnuler: () => void;
  onConfirmer: () => void;
}

// Ton adapté à ce qui est sélectionné : léger pour un lot 100% passé (pas de
// bouton "plus tard" à contourner, juste une confirmation), plus appuyé dès
// qu'au moins une demande encore en cours est concernée (interrompt un suivi
// actif). Toujours réversible depuis la Corbeille — rappelé dans les deux cas.
function ModalConfirmerSuppression({ total, nbEnCours, onAnnuler, onConfirmer }: ModalConfirmerSuppressionProps) {
  const alerte = nbEnCours > 0;
  return (
    <div className="modal-overlay" onClick={onAnnuler}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Supprimer {total > 1 ? `ces ${total} demandes` : 'cette demande'} ?</h3>
          <button className="close-btn" onClick={onAnnuler} aria-label="Fermer">✕</button>
        </div>
        <div className="modal-body">
          {alerte ? (
            <div className="card" style={{ background: 'var(--warn-dim)', borderColor: 'var(--warn)' }}>
              <div className="flex items-start gap-8">
                <span aria-hidden>⚠</span>
                <p className="text-sm text-ink-1" style={{ margin: 0 }}>
                  {nbEnCours > 1
                    ? `${nbEnCours} des ${total} demandes sélectionnées sont encore en cours.`
                    : 'Une des demandes sélectionnées est encore en cours.'}
                  {' '}Les supprimer interrompt leur suivi (recherche de remplaçant·e, passation…).
                  <br /><strong>Réversible</strong> depuis la Corbeille si besoin.
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-ink-2" style={{ margin: 0 }}>
              {total > 1 ? 'Toutes passées.' : 'Séance passée.'} Récupérable depuis la Corbeille si besoin.
            </p>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-sm btn-ghost" onClick={onAnnuler}>Annuler</button>
          <button type="button" className="btn btn-sm btn-danger" onClick={onConfirmer}>Supprimer</button>
        </div>
      </div>
    </div>
  );
}

function StatCarte({ valeur, label, active, onClick, valeurStyle }: {
  valeur: number; label: string; active: boolean; onClick: () => void; valeurStyle?: CSSProperties;
}) {
  return (
    <div
      className="stat-card"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      style={{ cursor: 'pointer', borderColor: active ? 'var(--accent)' : undefined, boxShadow: active ? 'inset 0 0 0 1px var(--accent)' : undefined }}
    >
      <div className="stat-value" style={valeurStyle}>{valeur}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}
